import React, {useEffect, useMemo, useState}					from 'react';
import {useNavigate}								from 'react-router-dom';
import moment										from 'moment';
import jschardet									from 'jschardet';

import Button										from '../ressources/components/Button';
import Input										from '../ressources/components/Input';
import Tabs											from '../ressources/components/Tabs';
import Select										from '../ressources/components/Select';
import Icon											from '../ressources/components/Icon';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import DocumentItem									from '../ressources/components/Document';
import Section										from '../ressources/components/Section';
import Modal										from '../ressources/components/Modal';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';
import Avatar										from '../ressources/components/Avatar';
import UsersModalAssignDevice						from './UserAssignDevice';
import DevicesBadges								from '../ressources/components/Badge/DevicesBadges';
import {DeviceCard}									from '../ressources/components/Device';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {PostUserData}								from '../redux/companyUsers/interface';
import {postCompanyUsersApi}						from '../redux/companyUsers/companyUsers';
import {assignDevicesApi}							from '../redux/devices/devices';
import {SelectData, SelectValues}					from '../ressources/components/Select/interface';
import {InputValues}								from '../ressources/components/Input/interface';

import {ft_genNotification, ft_pushNotification} 	from '../ressources/functions/notification';
import {ft_selectDataString}						from '../ressources/functions/selectData';
import {ft_fixText}									from '../ressources/functions/text';
import ft_cssVar									from '../ressources/functions/getCssVar';
import {ft_csvToArray}								from '../ressources/functions/file';

import img_computer									from '../ressources/images/icon/devices/computer.svg';
import img_roundCheck								from '../ressources/images/icon/check.svg';
import img_cross									from '../ressources/images/icon/close.svg';
import img_dotsVertical								from '../ressources/images/icon/dots-vertical.svg';
import img_users									from '../ressources/images/icon/users.svg';

import * as API										from '../ressources/API';
import LANGUAGE										from '../ressources/language';
import {regex_dateFormat}							from '../ressources/data/regex';

import './Users.css';

interface UserLine {
	firstname: string,
	lastname: string,
	email: string,
	job: string,
	phone: string,
	contractStart: string,
	contractEnd: string,
	assign: string[]
}

interface Error {
	isRequiredErr?: boolean
	text?: string,
	field: keyof UserLine
}


function UsersImport() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const lang = useAppSelector(state => state.settings.lang);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const users = useAppSelector(state => state.companyUsers.data);
	let notificationId = useAppSelector(state => state.notifications.lastId);

	const [step, set_step] = useState<number>(1);

	const [lineData, set_lineData] = useState<string[][]>([]);
	const [headerIndex, set_headerIndex] = useState<number[]>([]);
	const [error, set_error] = useState<string | undefined>();

	const [usersLine, set_usersLine] = useState<UserLine[]>([]);

	const [modalAssign, set_modalAssign] = useState<number | null>(null);
	const [modalActualAssign, set_modalActualAssign] = useState<number | null>(null);
	const [modalEdit, set_modalEdit] = useState<number | null>(null);

	const [load, set_load] = useState(false);

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const navigate = useNavigate();
	const TEXTE = LANGUAGE(lang);
	let foundErrorStep3 = false;

	const ACTIONS_LIST: SelectData[] = [
		{
			value:  'edit',
			render: <span className={'text-xs weight-500 gray-700'}>{TEXTE.global.edit}</span>
		},
		{
			value:  'assign',
			render: <span className={'text-xs weight-500 gray-700'}>{TEXTE.devices.assignDevices}</span>
		},
		{
			value:  'viewAssign',
			render: <span className={'text-xs weight-500 gray-700'}>{TEXTE.devices.seeDevices}</span>
		},
		{
			value:  'delete',
			render: (
				<span className={'text-xs weight-500 error-600'}>
					{TEXTE.global.delete}
				</span>
			)
		}
	];

	useEffect(() => {
		if (step === 3)
			csvToUserLine();
	}, [step]);

	async function addUsers() {
		let successCount = 0;
		set_load(true);

		await Promise.all(usersLine.map(userData =>
			new Promise((resolve) => {
				const apiData: PostUserData = {
					noHubspot:     true,
					addMethod:     'import',
					job:           userData.job,
					email:         userData.email,
					lastname:      userData.lastname,
					firstname:     userData.firstname,
					contractEnd:   userData.contractEnd ? moment(userData.contractEnd, 'DD/MM/YYYY').toDate() : undefined,
					contractStart: userData.contractStart ? moment(userData.contractStart, 'DD/MM/YYYY').toDate() : undefined
				};

				// --- Post User
				dispatch(postCompanyUsersApi({data: apiData, user}))
				.then(async (response) => {
					if (!response.payload._id) {
						const notificationData = ft_genNotification(TEXTE, 'error', img_users, response.payload.error, 'error');
						notificationId = ft_pushNotification(dispatch, notificationId, notificationData);
					} else
						successCount++;

					// --- Assign Devices
					if (userData.assign.length > 0) {
						await dispatch(assignDevicesApi({
							user,
							devicesId:  userData.assign,
							assignUser: response.payload
						}));
					}

					resolve(true);
				});
			})
		));

		const notificationData = ft_genNotification(TEXTE, 'addMultipleUsersSuccess', img_users, successCount.toString());
		notificationId = ft_pushNotification(dispatch, notificationId, notificationData);

		set_load(false);
		navigate('/users');
	}

	function readFile(files: FileList | null) {
		if (!files?.[0])
			return (set_error('Error while reading the file'));

		API.uploadDeviceImportDocument(files?.[0], user);

		const CHUNK_SIZE = 1024;
		let fullCsv = '';
		let offset = 0;

		const readNextChunk = () => {
			const blob = files[0].slice(offset, offset + CHUNK_SIZE);
			const reader = new FileReader();

			reader.onload = (event) => {
				if (!event.target?.result) {
					set_error('Error while reading the file');
					return;
				}

				const arrayBuffer = event.target.result as ArrayBuffer;
				const byteArray = String.fromCharCode(...new Uint8Array(arrayBuffer));
				const detectedEncoding = jschardet.detect(byteArray).encoding || 'utf-8';
				const decoder = new TextDecoder(detectedEncoding);
				const text = decoder.decode(arrayBuffer);

				fullCsv += text;
				offset += CHUNK_SIZE;

				if (offset < files[0].size)
					readNextChunk(); // Lire le prochain morceau
				else {
					const result = ft_csvToArray(fullCsv);

					// Fusionner les données au fur et à mesure
					set_lineData(result);

					// Détecter les en-têtes sur le premier fragment
					const resultIndex = [];
					for (let i = 0; result[0][i]; i++)
						resultIndex.push(i);
					set_headerIndex(resultIndex);

					set_step(2); // Lecture terminée
				}
			};

			reader.onerror = () => set_error('Error while reading the file');

			reader.readAsArrayBuffer(blob);
		};

		readNextChunk();
	}

	function checkStepIsValid() : boolean {
		if (step === 2)
			return headerIndex[0] > -1 && headerIndex[1] > -1 && headerIndex[2] > -1;

		if (step === 3)
			return !foundErrorStep3;

		return false;
	}

	function csvToUserLine() {
		const lines: UserLine[] = [];

		lineData.forEach((line, i) => {
			if (i === 0)
				return;

			lines.push({
				assign:        [],
				firstname:     line[headerIndex[0]],
				lastname:      line[headerIndex[1]],
				email:         line[headerIndex[2]],
				job:           line[headerIndex[3]],
				phone:         line[headerIndex[4]],
				contractStart: line[headerIndex[5]],
				contractEnd:   line[headerIndex[6]]
			});
		});

		set_usersLine(lines);
	}

	function checkError(line: UserLine) {
		const importStartDate = ft_fixText(line.contractStart);
		const importEndDate = ft_fixText(line.contractEnd);
		const errorLine: Error[] = [];

		if (!ft_fixText(line.firstname))
			errorLine.push({isRequiredErr: true, field: 'firstname'});
		if (!ft_fixText(line.lastname))
			errorLine.push({isRequiredErr: true, field: 'lastname'});
		if (!ft_fixText(line.email))
			errorLine.push({isRequiredErr: true, field: 'email'});
		else if (users.some(find => find?.email.toLowerCase() === line.email.toLowerCase()))
			errorLine.push({text: TEXTE.users.importError.emailExist, field: 'email'});

		if (importStartDate && !regex_dateFormat.test(importStartDate))
			errorLine.push({text:  TEXTE.devices.importError.mustBeDate, field: 'contractStart'});
		if (importEndDate && !regex_dateFormat.test(importEndDate))
			errorLine.push({text: TEXTE.devices.importError.mustBeDate, field: 'contractEnd'});

		return errorLine;
	}

	function renderStep1() {
		return (
			<div>
				<Section style={{gap: 8}}>
					<span className={'text-sm weight-500 gray-700'}>{TEXTE.devices.downloadTemplate}</span>
					<a href={'/Template Valala - Ajout par lot Employé.xlsx'} className={'no-underline'} >
						<DocumentItem
							hideActionIcon
							size={57948}
							filename={'Template Valala - Ajout par lot Employé.xlsx'}
						/>
					</a>
				</Section>

				<Section style={{gap: 8}}>
					<span className={'text-sm weight-500 gray-700'}>{TEXTE.devices.uploadCsv}</span>
					<Input
						error={error}
						type={'file'}
						accept={'.csv'}
						onChangeFile={readFile}
						fileInputText={TEXTE.devices.onlyCSV}
					/>
				</Section>
			</div>
		);
	}

	function renderStep2() {
		const renderTable: React.ReactElement[] = [];

		TEXTE.users.importFileHeader.forEach((_, i) => {
			const index = headerIndex[i];
			const exemple = lineData.find((find, i2) => i2 !== 0 && find[index]);

			renderTable.push(
				<tr className={''}>
					<td className={'text-xs gray-600'}>
						<span className={'weight-600'}>{TEXTE.users.importFileHeader[i]?.name}</span><br />
						<span className={'weight-400'}>{TEXTE.users.importFileHeader[i]?.exemple}</span>
					</td>
					<td>
						<div>
							<Select
								value={lineData[0][index]}
								data={ft_selectDataString(['', ...lineData[0]])}
								onChange={(newValue) => {
									const indexValue = lineData[0].findIndex(find => newValue !== '' && find === newValue);
									const copy = [...headerIndex];

									copy[i] = indexValue;
									set_headerIndex(copy);
								}}
							/>
						</div>
					</td>
					<td>
						<div className={'text-xs wight-400 gray-600'}>{exemple?.[index]}</div>
					</td>
					<td>
						<Icon
							iconBorder
							size={16}
							iconSize={8}
							borderSize={0}
							iconColor={'#ffffff'}
							icon={index > -1 ? img_roundCheck : img_cross}
							style={{backgroundColor: ft_cssVar(`${index > -1 ? 'success' : 'error'}-600`)}}
						/>
					</td>
				</tr>
			);
		});

		return (
			<>
				<span className={'gray-600'}>
					<span className={'weight-500 gray-700'}>{TEXTE.devices.import.howItsWork}</span>
					<li style={{marginTop: 8}}>{TEXTE.devices.import.step2Text1}</li>
					<li>{TEXTE.devices.import.step2Text2}</li>
				</span>

				<table className={'importTable'}>
					<thead>
						<tr className={'text-xs weight-500 gray-600'} style={{textAlign: 'left'}}>
							<th>{TEXTE.devices.import.propertyName}</th>
							<th>{TEXTE.devices.import.columnName}</th>
							<th>{TEXTE.devices.import.exemple}</th>
							<th style={{width: '10%'}}>{TEXTE.devices.import.status}</th>
						</tr>
					</thead>
					<tbody>
						{renderTable}
					</tbody>
				</table>
			</>
		);
	}

	function renderStep3() {
		const renderTable: React.ReactElement[] = [];

		usersLine.forEach((userData, i) => {
			const foundError = checkError(userData);

			if (foundError.length > 0)
				foundErrorStep3 = true;

			renderTable.push(
				<tr className={''}>
					<td>
						<div>
							<Avatar firstname={userData.firstname} lastname={userData.lastname} />
							<span className={'text-xs weight-500 gray-900'}>{userData.firstname} {userData.lastname}</span>{i}
						</div>
					</td>
					<td>{userData.job}</td>
					<td>{userData.email}</td>
					<td>{userData.contractStart}</td>
					<td>{userData.contractEnd}</td>
					<td>
						{usersLine.length < 1000 && <div><DevicesBadges devicesId={userData.assign} /></div>}
					</td>
					<td>
						<Button
							absoluteRight
							absoluteHideIcon
							type={'tertiary-gray'}
							absolute={ACTIONS_LIST}
							onChangeAbsolute={(action) => {
								if (action === 'delete') {
									const copy = [...usersLine];
									copy.splice(i, 1);

									return set_usersLine(copy);
								} else if (action === 'edit')
									return set_modalEdit(i);
								else if (action === 'assign')
									return set_modalAssign(i);
								else if (action === 'viewAssign')
									return set_modalActualAssign(i);
							}}
						>
							<div className={'flex'}>
								<Icon icon={img_dotsVertical} />
								{foundError.length > 0 && <div className={'headerNotif'} />}
							</div>
						</Button>
					</td>
				</tr>
			);
		});

		return (
			<>
				<span className={'gray-600'}>
					<span className={'weight-500 gray-700'}>{TEXTE.global.about}</span>
					<li style={{marginTop: 8}}>{TEXTE.devices.import.step3Text1}</li>
					<li>{TEXTE.devices.import.step3Text2}</li>
				</span>

				<table className={'importTable'}>
					<thead>
						<tr style={{textAlign: 'left'}}>
							<th>{TEXTE.global.name}</th>
							<th>{TEXTE.global.job}</th>
							<th>{TEXTE.global.email}</th>
							<th>{TEXTE.global.contractStart}</th>
							<th>{TEXTE.global.contractEnd}</th>
							<th>{TEXTE.global.devices}</th>
							<th style={{width: '10%'}}>{TEXTE.global.action}</th>
						</tr>
					</thead>
					<tbody>
						{renderTable}
					</tbody>
				</table>
			</>
		);
	}

	function renderButtonContainer() {
		return (
			<ButtonStickyContainer>
				<Button
					fullWidth
					disabled={load}
					type={'tertiary-gray'}
					onClick={() => (step === 1 ? navigate('/users/onboarding') : set_step(1))}
				>
					{TEXTE.global.cancel}
				</Button>

				<Button
					fullWidth
					isLoad={load}
					disabled={!checkStepIsValid()}
					onClick={checkStepIsValid() ?
						() => (step === 3 ? addUsers() : set_step(step + 1))
						:
						undefined
					}
				>
					{step === 3 ? TEXTE.global.confirm : TEXTE.global.next}
				</Button>
			</ButtonStickyContainer>
		);
	}

	function renderModalEdit() {
		if (modalEdit === null || !usersLine[modalEdit])
			return;

		const renderInput = (title: string, name: keyof UserLine, isDate?: boolean) => {
			if (name === 'assign')
				return;

			const onChange = (newValue: InputValues | SelectValues) => {
				const copy = [...usersLine];

				copy[modalEdit][name] = newValue as string;

				set_usersLine(copy);
			};
			const inputError = foundError.filter(e => e.field === name);

			return (
				<Input
					title={title}
					onChange={onChange}
					type={isDate ? 'date' : 'text'}
					value={usersLine[modalEdit][name]}
					error={inputError.map(e => e.text).join('')}
					required={!!inputError.find(find => find.isRequiredErr)}
				/>
			);
		};

		const foundError = checkError(usersLine[modalEdit]);

		return (
			<Modal
				fullWidth
				onClose={() => set_modalEdit(null)}
				cancelButton={() => set_modalEdit(null)}
				confirmButton={() => set_modalEdit(null)}
			>
				<Section style={{gap: 16}}>
					<MultiInputContainer>
						{renderInput(TEXTE.global.firstname, 'firstname')}
						{renderInput(TEXTE.global.lastname, 'lastname')}
					</MultiInputContainer>
					<MultiInputContainer>
						{renderInput(TEXTE.global.email, 'email')}
						{renderInput(TEXTE.global.job, 'job')}
					</MultiInputContainer>
					<MultiInputContainer>
						{renderInput(TEXTE.global.contractStart, 'contractStart', true)}
						{renderInput(TEXTE.global.contractEnd, 'contractEnd', true)}
					</MultiInputContainer>
				</Section>
			</Modal>
		);
	}

	function renderModal() {
		if (modalAssign !== null) {
			return (
				<UsersModalAssignDevice
					modal
					onClose={() => set_modalAssign(null)}
					deviceList={(
						devicesWithLines.filter(device => (
							!usersLine.some((e, i) => i !== modalAssign && e.assign.some(e2 => e2 === device._id))
						))
					)}
					onSave={(newAssign) => {
						const copy = [...usersLine];

						copy[modalAssign].assign = newAssign;

						const notificationData = ft_genNotification(TEXTE, 'assignDeviceSuccess', img_computer);
						notificationId = ft_pushNotification(dispatch, notificationId, notificationData);

						set_usersLine(copy);
						set_modalAssign(null);
					}}
				/>
			);
		}

		if (modalActualAssign !== null) {
			const render: React.ReactElement[] = [];

			devicesWithLines.forEach((device) => {
				if (!usersLine[modalActualAssign].assign.some(e => e === device._id))
					return;

				render.push(
					<DeviceCard hideBadge hideAssign device={device} />
				);
			});

			return (
				<Modal
					size={798}
					confirmButtonText={TEXTE.global.close}
					onClose={() => set_modalActualAssign(null)}
					confirmButton={() => set_modalActualAssign(null)}
					header={
						<Icon
							iconBorder
							size={36}
							iconSize={24}
							borderSize={8}
							type={'success'}
							icon={img_computer}
							iconColor={ft_cssVar('success-600')}
						/>
					}
				>
					<span className={'text-lg weight-600 gray-900'}>
						{`${TEXTE.users.assignTo} ${usersLine[modalActualAssign].firstname} ${usersLine[modalActualAssign].lastname}`}
					</span>
					{render}
				</Modal>
			);
		}

		return renderModalEdit();
	}

	return (
		<div className={'globalDataContainer'}>
			<div className={'globalAddHeader'}>
				<Icon
					icon={img_computer}
					iconColor={'#000000'}
				/>
				<div className={'globalAddHeaderText'}>
					<span>{TEXTE.users.addUserImport}</span>
					<span>{TEXTE.devices.uploadCsv}</span>
				</div>
			</div>

			<div className={'globalAddContainer'}>
				<Tabs
					step
					actual={step}
					onChange={val => set_step(val as number)}
					data={[
						{value: 1, render: TEXTE.devices.yourFile},
						{value: 2, render: TEXTE.devices.mapping},
						{value: 3, render: TEXTE.devices.assignDevices}
					]}
				/>

				{step === 1 && renderStep1()}
				{step === 2 && renderStep2()}
				{step === 3 && renderStep3()}
			</div>

			{renderButtonContainer()}
			{renderModal()}
		</div>
	);
}

export default UsersImport;